import { t } from "i18next";
import { useState } from "react";
import { buildingLinks } from "../../../api/adminDirectory";
import { deleteBuilding } from "../../../api/building";
import { enumsectionToI18nKey } from "../../../generated/enums";
import {
  Account,
  Building,
  DeviceWithoutLegacyPassword,
} from "../../../generated/types";
import { useAdminDirectory } from "../../../hooks/useAdminDirectory";
import { isAdminAccount } from "../../../utils/roles";
import { TableDetails, TicketNote } from "../../common/TableDetails";
import { TitleSection } from "../../common/TitleSection";
import { sectionToIcon } from "../../type/default";
import { BreedingSection } from "../breeding/BreedingSection";
import { BreedingTicket } from "../breeding/BreedingTicket";
import { DeleteEmptyModal } from "../breeding/DeleteEmptyModal";
import { DeviceSection } from "../device/DeviceSection";
import { DeviceTicket } from "../device/DeviceTicket";
import { AccountGroups } from "../user/AccountGroups";
import { CompanySection } from "../user/CompanySection";
import { UserTicket } from "../user/UserTicket";
import { AddDeviceToBuildingModal } from "./AddDeviceToBuildingModal";
import { BuildingSection } from "./BuildingSection";

type BuildingTicketProps = {
  building: Building;
  onClose: () => void;
  /** Rafraîchit la page sous les tickets. */
  onDataRefresh?: () => void;
};

export const BuildingTicket = ({
  building: initialBuilding,
  onClose,
  onDataRefresh,
}: BuildingTicketProps) => {
  const [selectedUser, setSelectedUser] = useState<Account | null>(null);
  const [selectedDevice, setSelectedDevice] =
    useState<DeviceWithoutLegacyPassword | null>(null);
  const [showBreeding, setShowBreeding] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddDeviceOpen, setIsAddDeviceOpen] = useState(false);

  const { directory, isLoading, failed, reload } = useAdminDirectory();
  const {
    building,
    breeding,
    company,
    accounts,
    installers,
    devices,
    deviceCount,
  } = buildingLinks(directory, initialBuilding);
  const refresh = () => {
    reload();
    onDataRefresh?.();
  };

  return (
    <div className="space-y-4">
      <TableDetails
        title={t(enumsectionToI18nKey["section_building"])}
        icon={sectionToIcon.section_building}
        onClose={onClose}
        onDelete={isAdminAccount() ? () => setIsDeleteOpen(true) : undefined}
        deleteText={t("building-delete")}
        deleteDisabledText={
          deviceCount > 0 ? t("building-delete-not-empty") : ""
        }
        useBackIcon
      >
        <div className="p-8 space-y-4">
          <BuildingSection
            building={building}
            detailLevel="maxi"
            onUpdate={refresh}
          />

          {isLoading || failed ? (
            <TicketNote>
              {failed
                ? String(t("ticket-links-error"))
                : `${String(t("loading"))}…`}
            </TicketNote>
          ) : (
            <>
              <TitleSection
                title={String(t(enumsectionToI18nKey["section_devices"]))}
                icon={sectionToIcon.section_device}
                onClick={
                  deviceCount === 0 && breeding
                    ? () => setIsAddDeviceOpen(true)
                    : undefined
                }
                actionLabel={String(t("building-add-device"))}
              />
              {devices.length > 0 ? (
                <div className="border-l-2 border-secondary pl-4 ml-2 space-y-4">
                  {devices.map((device) => (
                    <DeviceSection
                      key={device.id}
                      device={device}
                      detailLevel={devices.length > 3 ? "mini" : "default"}
                      onClick={() => setSelectedDevice(device)}
                    />
                  ))}
                </div>
              ) : (
                <TicketNote>{String(t("ticket-no-device"))}</TicketNote>
              )}

              <TitleSection
                title={String(t(enumsectionToI18nKey["section_breeding"]))}
                icon={sectionToIcon.section_breeding}
              />
              {breeding ? (
                <div className="border-l-2 border-secondary pl-4 ml-2">
                  <BreedingSection
                    breeding={breeding}
                    onClick={() => setShowBreeding(true)}
                  />
                </div>
              ) : (
                <TicketNote>{String(t("ticket-no-breeding"))}</TicketNote>
              )}

              <TitleSection
                title={String(t(enumsectionToI18nKey["section_accounts"]))}
                icon={sectionToIcon.section_accounts}
              />
              {accounts.length + installers.length > 0 ? (
                <AccountGroups
                  accounts={[...accounts, ...installers]}
                  directory={directory}
                  onSelect={setSelectedUser}
                />
              ) : (
                <TicketNote>{String(t("ticket-no-account"))}</TicketNote>
              )}
              {company && <CompanySection company={company} />}
            </>
          )}
        </div>
      </TableDetails>

      {selectedUser && (
        <UserTicket
          account={selectedUser}
          onClose={() => setSelectedUser(null)}
          onDataRefresh={onDataRefresh}
        />
      )}

      {selectedDevice && (
        <DeviceTicket
          device={selectedDevice}
          onClose={() => setSelectedDevice(null)}
          onDataRefresh={onDataRefresh}
        />
      )}

      {showBreeding && breeding && (
        <BreedingTicket
          breeding={breeding}
          onClose={() => setShowBreeding(false)}
          onDataRefresh={onDataRefresh}
        />
      )}

      {isAddDeviceOpen && breeding && (
        <AddDeviceToBuildingModal
          building={building}
          breedingName={breeding.name}
          companyId={breeding.id_company}
          companyName={company?.company_name ?? ""}
          onClose={() => {
            setIsAddDeviceOpen(false);
            refresh();
          }}
        />
      )}

      <DeleteEmptyModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title={t("building-delete")}
        warningText={t("building-delete-warning", { name: building.name })}
        // Les lots ne sont pas connus ici : l'API les signale par un refus.
        blockedText=""
        onDelete={() => deleteBuilding(building.id)}
        onDeleted={() => {
          setIsDeleteOpen(false);
          onClose();
          refresh();
        }}
      />
    </div>
  );
};
